<template>
  <div class="absolute-calendar-values">
    <PmwParagraph
      size="paragraph-80"
      weight="medium"
      class="absolute-calendar-values--title"
    >
      {{ roomData.name }}
    </PmwParagraph>

    <!-- ADULTOS -->
    <div class="absolute-calendar-values--wrapper">
      <PmwFormCurrencyInput
        v-for="item in adultInputItems"
        v-model="inputValue.adults[item.id]"
        :key="item.id"
        :label="item.label"
        :required="required"
        suffix="BRL"
        placeholder="000"
        size="small"
        type="number"
        @input="eventEmitter(inputValue.adults[item.id], item, roomData.codigo)"
      />
    </div>

    <!-- CRIANÇAS-->
    <div class="absolute-calendar-values--wrapper">
      <PmwFormCurrencyInput
        v-for="item in childrenInputItems"
        v-model="inputValue.children[item.id]"
        :key="item.id"
        :label="item.label"
        :required="required"
        suffix="BRL"
        placeholder="000"
        size="small"
        type="number"
        @input="
          eventEmitter(inputValue.children[item.id], item, roomData.codigo)
        "
      />
    </div>
  </div>
</template>

<script>
import PmwFormCurrencyInput from '@solucoes/components-lib-tmp/src/components/patterns/PmwFormCurrencyInput'
import PmwParagraph from '@solucoes/components-lib-tmp/src/components/elements/PmwParagraph'

export default {
  name: 'AbsoluteCalendarInput',

  components: {
    PmwFormCurrencyInput,
    PmwParagraph
  },

  props: {
    value: {
      type: Array,
      default: () => []
    },

    roomData: {
      type: Object,
      default: () => {}
    },

    adults: {
      type: Number,
      default: 1
    },

    children: {
      type: Number,
      default: 1
    }
  },

  data() {
    return {
      inputValue: { adults: [], children: [] }
    }
  },

  computed: {
    /** Cria os campos de input de adultos conforme o máximo de adultos permitido */
    adultInputItems() {
      const arr = []

      for (let i = 0; i < this.adults; i++) {
        arr.push({
          id: i,
          label: this.setLabels(i + 1, 'adulto'),
          numeroOcupantes: i + 1,
          tipoPax: 10
        })
      }

      return arr
    },

    /** Cria os campos de input de crianças conforme o máximo de crianças permitido*/
    childrenInputItems() {
      const arr = []

      for (let i = 0; i < this.children; i++) {
        arr.push({
          id: i,
          label: this.setLabels(i + 1, 'criança'),
          numeroOcupantes: i + 1,
          tipoPax: 8
        })
      }

      return arr
    },

    /** Se um dos campos for preenchido, os demais recebem o sinal de obrigatório */
    required() {
      return (
        this.inputValue.adults.some((item) => item !== '' && item !== null) ||
        this.inputValue.children.some((item) => item !== '' && item !== null)
      )
    },

    totalInputs() {
      return this.adults + this.children
    }
  },

  watch: {
    /** Passa as alterações do value para os inputs */
    value: {
      handler(newVal) {
        if (newVal.length) {
          newVal.forEach((item) => {
            /** tipoPax === 10 = adultos */
            if (item.tipoPax === 10) {
              this.inputValue.adults[item.numeroOcupantes - 1] = item.valor
            } else {
              this.inputValue.children[item.numeroOcupantes - 1] = item.valor
            }
          })

          this.checkAllFields()
        }
      },

      deep: true,
      immediate: true
    }
  },

  methods: {
    /** Verifica se todos os campos estão preenchidos e emite um evento */
    checkAllFields() {
      /** Verifica se o value tem todos os campos */
      const hasAllFields = this.value.length === this.totalInputs

      /** Verifica se todos estão vazios (apagados) */
      const areAllEmpty = this.value.every(
        (item) => item.valor === '' || item.valor === null
      )

      /** Verifica se apenas alguns estão vazios */
      const areSomeEmpty = this.value.some(
        (item) => item.valor === '' || item.valor === null
      )

      /**
       * Se tem todos os campos E
       * tdos estão preenchidos OU
       * se tem todos os campos E
       * tdos estão vazios OU
       * se não tem todos os campos E
       * tds estão vazios
       */
      if (
        (hasAllFields && !areSomeEmpty) ||
        (hasAllFields && areAllEmpty) ||
        (!hasAllFields && areAllEmpty)
      ) {
        this.$emit('hasAllFields', true)
      } else {
        this.$emit('hasAllFields', false)
      }
    },

    /**
     * Seta os labels dos inputs
     * @param {number} number: número de pessoas
     * @param {string} text: texto do label
     * @returns texto com o número de pessoas e o texto
     */
    setLabels(number, text) {
      let label = ''

      if (number === 1) {
        label = `Valor para ${number} ${text}`
      } else {
        label = `Valor para ${number} ${text}s`
      }

      return label
    },

    /**
     * Emite os eventos de input e atualiza o value
     * @param {string} inputValue: valor inserido
     * @param {number} item.tipoPax: ID do pax (adultos ou crianças)
     * @param {number} item.numeroOcupantes: número de pessoas
     * @param {number} codigo: ID do quarto
     */
    eventEmitter(inputValue, item, codigo) {
      /** Verifica se no value já foi inserido valor para aquele pax e número de ocupantes */
      const idx = this.value.findIndex(
        (el) =>
          el.tipoPax === item.tipoPax &&
          el.numeroOcupantes === item.numeroOcupantes
      )

      /** Se sim, atualiza o valor */
      if (idx > -1) {
        this.value[idx].valor = inputValue
      } else {
        /** Se não, insere um novo item ao value */
        this.value.push({
          numeroOcupantes: item.numeroOcupantes,
          tipoPax: item.tipoPax,
          valor: inputValue
        })
      }

      this.$emit('inputEvent', this.value, codigo)
    }
  }
}
</script>

<style lang="scss" scoped>
.absolute-calendar {
  &-values {
    border: 1px $pmw-gray-border solid;
    padding: 25px;
    margin-bottom: 25px;

    &--title {
      margin-bottom: 15px;
    }

    &--wrapper {
      display: flex;
      gap: 20px;
      flex-wrap: wrap;
      margin-bottom: 20px;
    }
  }
}
</style>
